import React, { FC } from 'react';
import {
  TypeCtaWithTrailingTextFields,
  TypeImage,
  TypeMediaAndTextSideBySideFields,
} from '@/core/generated-types';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import classNames from 'classnames';
import { getContentType } from '@/core/constants';
import {
  CTAWithTrailingText,
  Image,
  RichText,
  UniversalCta,
  Video,
} from '@/components';
import { Document } from '@contentful/rich-text-types';

export const MediaAndTextSideBySide: FC<TypeMediaAndTextSideBySideFields> = ({
  textHeading,
  textBody,
  mediaLeft,
  image,
  video,
  entryName,
  cta,
  backgroundColor,
  maxWidth,
  textAlign,
  leadingText,
  centerOnMobile = false,
  videoIsFourThreeAspect,
  containImage,
}) => {
  const textAlignClass = textAlign === 'right' ? 'text-right' : 'text-left';
  const flexAlignClass =
    textAlign === 'right' ? 'justify-end' : 'justify-start';
  return (
    <section
      data-testid={`media-and-text-side-by-side-${entryName}`}
      className={`bg-${backgroundColor?.fields?.color}`}
    >
      <div style={{ maxWidth }} className="mx-auto h-full">
        <div
          className={`flex h-full flex-col md:flex-row ${
            mediaLeft ? '' : 'md:flex-row-reverse'
          }`}
        >
          <div
            className={classNames(
              'relative flex-1 md:w-1/2',
              video ? '' : "before:block before:pt-[100%] before:content-['']",
            )}
          >
            {video ? (
              <Video
                classes={classNames(
                  'h-full object-cover w-full',
                  videoIsFourThreeAspect ? 'aspect-[4/3]' : 'aspect-[1/1]',
                )}
                src={video.fields.file.url}
                autoplay={true}
                loop={true}
                muted={true}
                playsinline={true}
              />
            ) : (
              <Image
                imageData={image as TypeImage}
                className={classNames(
                  'absolute left-0 top-0 h-full w-full',
                  containImage ? 'object-contain' : 'object-cover',
                )}
                alt={image?.fields?.altText}
              />
            )}
          </div>
          <div
            className={classNames(
              'box-border flex flex-1 flex-col justify-center px-6 pb-10 pt-8 md:w-1/2 md:max-w-[50%] md:px-16 lg:px-24 ',
              {
                [`text-center md:${textAlignClass}`]: centerOnMobile,
                [textAlignClass]: !centerOnMobile,
              },
            )}
          >
            <div>
              {leadingText && (
                <div className="mb-4 text-base text-purple md:text-lg">
                  {leadingText}
                </div>
              )}
              <HeaderText size={HeaderSize.H2} text={textHeading} />
              <div
                className="mt-3 text-base md:mt-6 md:text-lg"
                data-testid="media-text-body"
              >
                {textBody && <RichText content={textBody as Document} />}
              </div>
              {cta && (
                <div
                  className={classNames('flex pt-6', {
                    [`justify-center md:${flexAlignClass}`]: centerOnMobile,
                    [flexAlignClass]: !centerOnMobile,
                  })}
                >
                  {getContentType(cta) === 'ctaWithTrailingText' ? (
                    <CTAWithTrailingText
                      isCenterAligned={false}
                      {...(cta?.fields as TypeCtaWithTrailingTextFields)}
                    />
                  ) : (
                    <UniversalCta link={cta as any} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
